/**
 * WebXR 手柄 / 手部追踪输入。
 *
 * 把设备压成和桌面完全一致的一组「轴 + 边沿」（`InputSnapshot` 的语义），
 * 这样 `LocalPlayer` 不需要知道自己跑在 VR 还是桌面。
 *
 * 手柄按键索引按 `xr-standard` 映射（Quest Touch 吻合）：
 *   0 扳机 / 1 抓握 / 3 摇杆按下 / 4 A或X / 5 B或Y
 * 摇杆在 `axes[2]`、`axes[3]`（touchpad 占 0、1）。
 *
 * 手部追踪（没有手柄）没有按键，用「拇指尖 ↔ 食指尖」的距离做捏合判定，
 * 捏合等于按下扳机。
 *
 * 没有头显也能验证：`update()` 只依赖 `session.inputSources` 的形状，
 * 所以调试时可以直接喂一个假 session（见 `__sea.xrInputTest`）。
 */

export interface XRInputState {
  /** 前后：+1 前推 */
  forward: number;
  /** 左右：+1 向右 */
  strafe: number;
  /** 吸附转身：-1 左、+1 右、0 不转（边缘触发，摇杆回中后才会再次触发） */
  snapTurn: number;
  /** 以下都是"本帧刚按下" */
  sit: boolean;
  wave: boolean;
  palm: boolean;
  mute: boolean;
  /** 手部追踪：捏合是否刚闭合 */
  pinch: boolean;
  /** 扳机 / 抓握的模拟值（0..1，左右手各一份），用来驱动手指弯曲 */
  triggerLeft: number;
  triggerRight: number;
  gripLeft: number;
  gripRight: number;
}

const DEADZONE = 0.18;
const SNAP_ON = 0.7;
const SNAP_OFF = 0.3;
/** 捏合判定（米）：闭合 / 张开两档，避免临界抖动 */
const PINCH_ON = 0.022;
const PINCH_OFF = 0.034;

type ButtonLike = { value?: number; pressed?: boolean };
type GamepadLike = {
  axes?: ReadonlyArray<number>;
  buttons?: ReadonlyArray<ButtonLike>;
};
type Vec3Like = { x: number; y: number; z: number };
type JointPoseLike = { transform: { position: Vec3Like } };
type JointSpaceLike = unknown;
type HandLike = { get?: (joint: string) => JointSpaceLike | undefined };
type SourceLike = {
  handedness?: string;
  gamepad?: GamepadLike | null;
  hand?: HandLike | null;
};
type SessionLike = { inputSources?: ArrayLike<SourceLike> };
type FrameLike = {
  getJointPose?: (joint: JointSpaceLike, refSpace: unknown) => JointPoseLike | null;
};
type RendererLike = {
  xr: {
    getSession?: () => SessionLike | null;
    getReferenceSpace?: () => unknown;
  };
};

function buttonValue(gp: GamepadLike, index: number): number {
  const b = gp.buttons?.[index];
  if (!b) return 0;
  if (typeof b.value === 'number') return b.value;
  return b.pressed ? 1 : 0;
}

function applyDeadzone(v: number): number {
  if (Math.abs(v) < DEADZONE) return 0;
  // 去掉死区后重新映射到 0..1，避免"刚过死区就全速"
  const sign = Math.sign(v);
  return sign * ((Math.abs(v) - DEADZONE) / (1 - DEADZONE));
}

export class XRInput {
  private prevSit = false;
  private prevWave = false;
  private prevPalm = false;
  private prevMute = false;
  private snapArmed = true;
  private pinchClosed = false;

  /** 手部追踪没有手柄按键，用「拇指尖 ↔ 食指尖」的距离判捏合 */
  private static pinchDistance(
    src: SourceLike,
    frame: FrameLike | null,
    refSpace: unknown,
  ): number | null {
    if (!src.hand?.get || !frame?.getJointPose || !refSpace) return null;
    const thumb = src.hand.get('thumb-tip');
    const index = src.hand.get('index-finger-tip');
    if (!thumb || !index) return null;
    const a = frame.getJointPose(thumb, refSpace);
    const b = frame.getJointPose(index, refSpace);
    if (!a || !b) return null;
    const dx = a.transform.position.x - b.transform.position.x;
    const dy = a.transform.position.y - b.transform.position.y;
    const dz = a.transform.position.z - b.transform.position.z;
    return Math.hypot(dx, dy, dz);
  }

  update(renderer: RendererLike, frame: FrameLike | null = null): XRInputState {
    const out: XRInputState = {
      forward: 0,
      strafe: 0,
      snapTurn: 0,
      sit: false,
      wave: false,
      palm: false,
      mute: false,
      pinch: false,
      triggerLeft: 0,
      triggerRight: 0,
      gripLeft: 0,
      gripRight: 0,
    };

    const session = renderer.xr.getSession?.() ?? null;
    const sources = session?.inputSources;
    if (!sources) return out;

    const refSpace = renderer.xr.getReferenceSpace?.() ?? null;

    let triggerLeft = 0;
    let triggerRight = 0;
    let gripLeft = 0;
    let gripRight = 0;
    let aOrX = false;
    let bOrY = false;
    let rightStickX = 0;
    let pinchClosed = this.pinchClosed;

    for (let i = 0; i < sources.length; i++) {
      const src = sources[i];
      if (!src) continue;

      // 手部追踪：没有 gamepad，走捏合判定
      const pinchDist = XRInput.pinchDistance(src, frame, refSpace);
      if (pinchDist !== null) {
        // 滞回：低于阈值算闭合，高于阈值算张开，中间保持上一帧
        if (pinchDist < PINCH_ON) pinchClosed = true;
        else if (pinchDist > PINCH_OFF) pinchClosed = false;
        continue;
      }

      const gp = src.gamepad;
      if (!gp) continue;

      const axes = gp.axes ?? [];
      const sx = axes.length >= 4 ? axes[2]! : (axes[0] ?? 0);
      const sy = axes.length >= 4 ? axes[3]! : (axes[1] ?? 0);
      const hand = src.handedness ?? 'none';

      if (hand === 'right') {
        rightStickX = sx;
      } else {
        // 左手（或 handedness 缺失）= 移动；摇杆前推是负 y
        out.strafe += applyDeadzone(sx);
        out.forward += -applyDeadzone(sy);
      }

      if (hand === 'right') triggerRight = Math.max(triggerRight, buttonValue(gp, 0));
      else triggerLeft = Math.max(triggerLeft, buttonValue(gp, 0));
      if (hand === 'right') gripRight = Math.max(gripRight, buttonValue(gp, 1));
      else gripLeft = Math.max(gripLeft, buttonValue(gp, 1));
      if (buttonValue(gp, 4) > 0.5) aOrX = true;
      if (buttonValue(gp, 5) > 0.5) bOrY = true;
    }

    out.forward = Math.max(-1, Math.min(1, out.forward));
    out.strafe = Math.max(-1, Math.min(1, out.strafe));

    // 捏合边沿：本帧闭合、上一帧没有
    const pinchEdge = pinchClosed && !this.pinchClosed;
    this.pinchClosed = pinchClosed;
    out.pinch = pinchEdge;

    // 边沿触发（捏合等同于扳机）
    out.triggerLeft = Math.min(1, triggerLeft);
    out.triggerRight = Math.min(1, triggerRight);
    out.gripLeft = Math.min(1, gripLeft);
    out.gripRight = Math.min(1, gripRight);
    const sitDown = Math.max(triggerLeft, triggerRight) > 0.5 || pinchEdge;
    out.sit = sitDown && !this.prevSit;
    out.wave = aOrX && !this.prevWave;
    out.palm = bOrY && !this.prevPalm;
    out.mute = Math.max(gripLeft, gripRight) > 0.5 && !this.prevMute;
    this.prevSit = Boolean(sitDown);
    this.prevWave = aOrX;
    this.prevPalm = bOrY;
    this.prevMute = Math.max(gripLeft, gripRight) > 0.5;

    // 右摇杆吸附转身：推到阈值才转一次，回中后才允许下一次
    if (Math.abs(rightStickX) > SNAP_ON) {
      if (this.snapArmed) {
        out.snapTurn = Math.sign(rightStickX);
        this.snapArmed = false;
      }
    } else if (Math.abs(rightStickX) < SNAP_OFF) {
      this.snapArmed = true;
    }

    return out;
  }
}

